import { useEffect, useState, useCallback, useRef } from 'react'
import { Routes, Route, useLocation, Navigate } from 'react-router-dom'
import { supabase } from '../integrations/supabase/client.safe'
import { AuthPage } from '../pages/AuthPage'
import { DashboardPage } from '../pages/DashboardPage'
import { CasesPage } from '../pages/CasesPage'
import { CaseDetailPage } from '../pages/CaseDetailPage'
import { FinalReportPage } from '../pages/FinalReportPage'
import { NewCasePage } from '../pages/NewCasePage'
import { QueuePage } from '../pages/QueuePage'
import { TeamPage } from '../pages/TeamPage'
import { BillingPage } from '../pages/BillingPage'
import { OrganizationSettings } from '../pages/OrganizationSettings'
import { SupportPage } from '../pages/SupportPage'
import { StatisticsPage } from '../pages/StatisticsPage'
import { OrganizationRegistration } from '../pages/OrganizationRegistration'
import { PoliciesPage } from '../pages/PoliciesPage'
import OrganizationSettingsNew from '../pages/OrganizationSettingsNew'
import NotFound from '../pages/NotFound'
import { TestlabPage } from '../pages/TestlabPage'
import { TestlabTaxAccountPage } from '../pages/TestlabTaxAccountPage'
import { TestlabPipelinePage } from '../pages/TestlabPipelinePage'
import TestlabPremiumEchoPage from '../pages/TestlabPremiumEchoPage'
import { TestlabFullAnalysisPage } from '../pages/TestlabFullAnalysisPage'
import { TestlabRealDataPage } from '../pages/TestlabRealDataPage'
import { TestlabRealDataBackendPage } from '../pages/TestlabRealDataBackendPage'
import { SidebarTrigger } from './ui/sidebar'
import { AppSidebar } from './AppSidebar'
import { Button } from './ui/button'
import { LogOut, User, MessageCircle } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { TrialWelcomeBanner } from '@/components/TrialWelcomeBanner'
import { LanguageSelector } from '@/components/LanguageSelector'
import type { Session } from '@supabase/supabase-js'
import { useTranslation } from 'react-i18next'

interface UserProfile {
  id: string;
  email?: string;
  full_name?: string;
  display_name?: string;
  avatar_url?: string;
  [key: string]: unknown;
}

interface Organization {
  id: string;
  name: string;
  company_name?: string;
  org_number?: string;
  login?: string;
  avatar_url?: string;
  [key: string]: unknown;
}

export function AppLayout() {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [loading, setLoading] = useState(true)
  const location = useLocation()
  const loadedForUserRef = useRef<string | null>(null)
  const { toast } = useToast()
  const { t } = useTranslation('common')

  const loadUserData = useCallback(async (userId: string, fullName?: string) => {
    try {
      // Ensure profile exists only once per session (but always allow org reloads)
      if (loadedForUserRef.current !== userId) {
        // Load or create profile (avoid throwing when not found)
        const { data: existingProfile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle()

        if (profileError) throw profileError

        if (!existingProfile) {
          const { data: newProfile, error: createError } = await supabase
            .from('profiles')
            .insert({
              id: userId,
              full_name: fullName ?? null
            })
            .select()
            .single()

          if (createError) throw createError
          setProfile(newProfile)
        } else {
          setProfile(existingProfile)
        }
        loadedForUserRef.current = userId
      }

      // Try to load user's organization data without relying on stale state
      try {
        // Helper to map DB row -> Organization shape
        const mapOrg = (row: any): Organization => ({
          id: row.id,
          name: row.company_name || row.name,
          company_name: row.company_name ?? undefined,
          org_number: row.org_number ?? undefined,
          login: row.name || row.company_name
        })

        const isValidUuid = (v: string) => /^[0-9a-fA-F-]{36}$/.test(v)

        let foundOrg: Organization | null = null

        // 1) Previously selected organization from localStorage
        const storedOrgId = localStorage.getItem('active-org-id')
        if (storedOrgId && isValidUuid(storedOrgId)) {
          const { data: orgData, error: orgFetchError } = await supabase
            .from('organizations')
            .select('*')
            .eq('id', storedOrgId)
            .maybeSingle()
          if (!orgFetchError && orgData) {
            foundOrg = mapOrg(orgData)
          } else if (orgFetchError) {
            console.warn('Failed to fetch stored organization:', orgFetchError)
          }
        } else if (storedOrgId && !isValidUuid(storedOrgId)) {
          localStorage.removeItem('active-org-id')
        }

        // 2) Role-based access (RPC)
        if (!foundOrg) {
          const { data: orgAccess, error: orgError } = await supabase
            .rpc('get_user_organization_access')

          if (!orgError && orgAccess && orgAccess.length > 0) {
            const targetId = (orgAccess.find((r: any) => r.organization_id)?.organization_id) as string | null
            if (targetId && isValidUuid(targetId)) {
              const { data: orgData, error: orgFetchError } = await supabase
                .from('organizations')
                .select('*')
                .eq('id', targetId)
                .maybeSingle()

              if (!orgFetchError && orgData) {
                foundOrg = mapOrg(orgData)
                localStorage.setItem('active-org-id', orgData.id)
              }
            }
          }
        }

        // 3) Fallback to org created by current user
        if (!foundOrg) {
          const { data: createdOrg } = await supabase
            .from('organizations')
            .select('*')
            .eq('created_by_user_id', userId)
            .maybeSingle()

          if (createdOrg) {
            foundOrg = mapOrg(createdOrg)
            localStorage.setItem('active-org-id', createdOrg.id)

            // Ensure user has admin role for this org so RPC works next time
            await supabase.from('user_roles').upsert({
              user_id: userId,
              organization_id: createdOrg.id,
              role: 'admin' as any
            } as any)
          }
        }

        // 4) Final fallback: default org
        if (!foundOrg) {
          foundOrg = {
            id: 'default-org',
            name: 'Din Organisation',
            company_name: 'Din Organisation',
            org_number: undefined,
            login: 'default'
          }
        }

        setOrganization(foundOrg)
      } catch (orgLoadError) {
        console.warn('Error loading organization:', orgLoadError)
        // Fallback to default organization
        setOrganization({ 
          id: 'default-org', 
          name: 'Din Organisation',
          company_name: 'Din Organisation',
          org_number: undefined,
          login: 'default'
        })
      }

    } catch (error: unknown) {
      console.error('Error loading user data:', error)
      toast({
        title: t('errors.generic'),
        description: t('errors.network'),
        variant: 'destructive'
      })
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }, [])


  useEffect(() => {
    // Listen for auth changes FIRST to avoid missing events and deadlocks
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      console.log('[AppLayout] Auth event:', event, s?.user?.id)
      setSession(s)

      if (s?.user?.id) {
        // Defer any Supabase calls to avoid deadlocks
        setTimeout(() => {
          loadUserData(s.user!.id, s.user?.user_metadata?.full_name)
        }, 0)
      } else {
        setProfile(null)
        setOrganization(null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [loadUserData])

  useEffect(() => {
    // Initial session check
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      console.log('[AppLayout] Initial session check:', session?.user?.id)
      setSession(session)
      if (session?.user?.id) {
        await loadUserData(session.user.id, session.user.user_metadata?.full_name)
      } else {
        setLoading(false)
      }
    }).catch((error) => {
      console.error('[AppLayout] Session check error:', error)
      setLoading(false)
    })
  }, [loadUserData])

  // Safety: never spin forever
  useEffect(() => {
    const timeout = setTimeout(() => setLoading(false), 6000)
    return () => clearTimeout(timeout)
  }, [])

  // Ensure we always have the freshest profile name (if empty, fetch latest)
  useEffect(() => {
    const userId = session?.user?.id
    if (!userId) return
    const name = profile?.full_name
    if (!name || name.trim() === '') {
      void (async () => {
        try {
          const { data } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .maybeSingle()
          if (data) setProfile(data)
        } catch (e) {
          // ignore
        }
      })()

    }
  }, [session?.user?.id, profile?.full_name])

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) {
      toast({
        title: t('errors.generic'),
        description: error.message,
        variant: 'destructive'
      })
    } else {
      toast({
        title: t('auth.signout'),
        description: t('auth.signout')
      })
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  // Show auth page if not logged in, except for organization registration
  if (!session || !profile) {
    // Allow access to organization registration without login
    if (location.pathname === '/register-organization') {
      return (
        <div className="min-h-screen bg-gray-50">
          <Routes>
            <Route path="/register-organization" element={
              <OrganizationRegistration 
                onSuccess={(orgId) => {
                  toast({
                    title: t('toast.success'),
                    description: t('toast.success')
                  })
                  // Redirect to auth page after successful registration
                  window.location.href = '/'
                }}
              />
            } />
          </Routes>
        </div>
      )
    }
    return <AuthPage />
  }

  // Main app layout with sidebar
  return (
    <div className="min-h-screen flex w-full bg-gray-50">
      {/* Sidebar for navigation */}
      <AppSidebar organization={organization} />
      
      {/* Main content area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="h-16 flex items-center justify-between px-6 bg-white border-b border-gray-200 shadow-sm">
          <div className="flex items-center gap-4">
            <SidebarTrigger />
            {organization && (
              <p className="text-sm text-gray-500">{organization.name}</p>
            )}
          </div>
          
          {/* Support and user controls */}
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.location.href = '/support'}
              className="gap-2"
            >
              <MessageCircle className="h-4 w-4" />
              {t('navigation.support')}
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <User className="h-4 w-4 text-blue-600" />
              </div>
              <div className="text-right text-sm">
                <p className="font-medium text-gray-900">
                  {profile.full_name || profile.email}
                </p>
                <p className="text-gray-500">{profile.email}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleSignOut}
              className="gap-2"
            >
              <LogOut className="h-4 w-4" />
              {t('profile.logout')}
            </Button>
          </div>
        </header>

        {/* Trial Welcome Banner */}
        <TrialWelcomeBanner className="mx-6 mt-4" />

        {/* Page content */}
        <main className="flex-1 overflow-auto p-6">
          <Routes>
            <Route path="/" element={<DashboardPage organization={organization} profile={profile} />} />
            <Route path="/cases" element={<CasesPage organization={organization} profile={profile} />} />
            <Route path="/cases/new" element={<NewCasePage organization={organization} profile={profile} />} />
            <Route path="/cases/:id" element={<CaseDetailPage organization={organization} profile={profile} />} />
            <Route path="/final-report/:id" element={<FinalReportPage organization={organization} profile={profile} />} />
            <Route path="/queue" element={<QueuePage organization={organization} profile={profile} />} />
            <Route path="/team" element={<TeamPage organization={organization} profile={profile} />} />
            <Route path="/billing" element={<BillingPage organization={organization} profile={profile} />} />
            <Route path="/policies" element={<PoliciesPage organization={organization} profile={profile} />} />
            <Route path="/settings" element={<OrganizationSettings organization={organization} />} />
            <Route path="/settings/organization" element={<OrganizationSettingsNew />} />
            <Route path="/statistics" element={<StatisticsPage />} />
            <Route path="/support" element={<SupportPage organization={organization} profile={profile} />} />
             <Route path="/testlab" element={<TestlabPage />} />
             <Route path="/testlab/tax-account" element={<TestlabTaxAccountPage />} />
             <Route path="/testlab/tax-pipeline" element={<TestlabPipelinePage />} />
             <Route path="/testlab/premium-echo" element={<TestlabPremiumEchoPage />} />
             <Route path="/testlab/full-analysis" element={<TestlabFullAnalysisPage />} />
             <Route path="/testlab/real-data" element={<TestlabRealDataPage />} />
             <Route path="/testlab/real-data-backend" element={<TestlabRealDataBackendPage />} />
            <Route path="/register-organization" element={
              <OrganizationRegistration 
                onSuccess={(orgId) => {
                  toast({
                    title: t('toast.success'),
                    description: t('toast.success')
                  })
                  // Redirect to team page to manage the new organization
                  window.location.href = '/team'
                }}
              />
            } />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}